import type { EmailAddress } from "../../services/graphMailService";

/**
 * SP3 (B3) — pure Helfer für den Outlook-artigen Mail-Kopf („Von" UND „An"
 * plus Tag/Datum/Uhrzeit). DOM-frei → testbar in mailHeader.test.ts.
 */

/**
 * Deutsche Kurz-Wochentage — bewusst eigene Tabelle statt
 * `toLocaleDateString(…, { weekday: "short" })`, damit die Ausgabe
 * („Fr, 03.07.2026, 14:32") deterministisch ist (keine ICU-/Punkt-Varianz).
 */
const WEEKDAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Kopf-Datumszeile: „Fr, 03.07.2026, 14:32" (lokale Zeit). */
export function formatHeaderDate(d: Date): string {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${WEEKDAYS[d.getDay()]}, ${dd}.${mm}.${d.getFullYear()}, ${hh}:${min}`;
}

/**
 * E-Mail-Adresse fürs UI normalisieren (Screenshot-Befund „DC@courte.com" →
 * lowercase + trim). Nur Anzeige — die Original-Adresse bleibt im Datenmodell.
 */
export function normalizeAddress(addr: string | null | undefined): string {
    return (addr ?? "").trim().toLowerCase();
}

/**
 * „An:"-/„Cc:"-Zeile als reiner Text: die ersten `max` Empfänger (Anzeigename,
 * sonst Adresse), der Rest als „+n"-Overflow. Leere Liste → „—" (klar
 * sichtbares Nichts statt einer nackten Beschriftung).
 *
 * Seit PO Runde 3 baut der Mail-Kopf die An-Zeile aus `recipientEntries` +
 * `splitRecipients` auf — Name und Adresse brauchen dort eigene Auszeichnung
 * und lassen sich nicht mehr als eine Zeichenkette ausgeben. Diese Funktion
 * bleibt für Textausgaben ohne Auszeichnung stehen; im Kopf ruft sie niemand
 * mehr.
 */
export function formatRecipientLine(
    recipients: EmailAddress[] | null | undefined,
    max = 3,
): string {
    const names = (recipients ?? [])
        .map((r) => (r?.name || r?.address || "").trim())
        .filter(Boolean);
    if (!names.length) return "—";
    const shown = names.slice(0, max).join(", ");
    const more = names.length - Math.min(names.length, max);
    return more > 0 ? `${shown} +${more}` : shown;
}

/**
 * Ein Empfänger, so wie der Kopf ihn zeigt.
 *
 * PO Runde 3 („bei Mail bei An die Adresse mit anzeigen"): Bei „Von:" steht die
 * Adresse längst dabei — sie IST die Auskunft, an der man jemanden festmacht
 * (zwei „Müller" unterscheiden sich nur daran). „An:" zeigte bis dahin nur
 * Namen; wer nachsehen wollte, an WELCHE Adresse eine Mail ging, musste raten.
 */
export interface RecipientDisplay {
    /** Anzeigename; leer, wenn die Mail nur eine Adresse trägt. */
    name: string;
    /** Adresse, kleingeschrieben (wie bei „Von:"); kann leer sein. */
    address: string;
}

/**
 * So viele Empfänger zeigt die eingeklappte „An:"-Zeile.
 *
 * Weniger als die früheren drei Namen, weil jeder Eintrag jetzt zusätzlich
 * seine Adresse trägt und damit rund dreimal so lang ist. Der Rest steht einen
 * Klick entfernt — dasselbe Muster wie bei Cc.
 */
export const RECIPIENT_COMPACT_MAX = 2;

/**
 * Empfängerliste fürs UI: leere Einträge fallen weg, die Adresse wird
 * normalisiert.
 *
 * Trägt Graph als Namen dieselbe Zeichenkette wie die Adresse (kommt bei
 * Verteilern vor), bleibt nur die Adresse übrig — sonst stünde sie zweimal da.
 */
export function recipientEntries(
    recipients: EmailAddress[] | null | undefined,
): RecipientDisplay[] {
    return (recipients ?? [])
        .map((r) => {
            const address = normalizeAddress(r?.address);
            const name = (r?.name ?? "").trim();
            return { name: name.toLowerCase() === address ? "" : name, address };
        })
        .filter((r) => r.name || r.address);
}

/** Ein Empfänger als Text: „Anna Alt ‹anna@example.com›" bzw. nur die Adresse. */
export function formatRecipient(r: RecipientDisplay): string {
    if (!r.name) return r.address;
    return r.address ? `${r.name} ‹${r.address}›` : r.name;
}

/**
 * Was die eingeklappte Zeile zeigt und wie viele dahinter noch stehen.
 *
 * `rest` ist die Zahl auf dem Schalter — ist sie 0, gibt es keinen Schalter.
 */
export function splitRecipients(
    entries: RecipientDisplay[],
    max = RECIPIENT_COMPACT_MAX,
): { shown: RecipientDisplay[]; rest: number } {
    const alle = entries ?? [];
    if (alle.length <= max) return { shown: alle, rest: 0 };
    return { shown: alle.slice(0, max), rest: alle.length - max };
}

/**
 * Vollständige Empfängerliste fürs `title`-Attribut — auch das, was die
 * eingeklappte Zeile abschneidet, ist damit ohne Klick lesbar.
 */
export function formatRecipientTitle(recipients: EmailAddress[] | null | undefined): string {
    return recipientEntries(recipients).map(formatRecipient).join(", ");
}
